/**
 * Storefront Hunter API client + response → UI bundle mapper.
 * Does not call Buy Sources, Oxylabs, or Google.
 */

import { del, get, post } from "@/lib/api";

/**
 * POST /storefront-hunter/analyze
 * @param {string} seller — Walmart seller URL or Seller ID
 * @param {{ page?: number, pageSize?: number }} [opts]
 */
export async function analyzeStorefront(seller, opts = {}) {
  const page = opts.page ?? 1;
  const page_size = opts.pageSize ?? 40;
  const qs = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
  });
  return post(`/storefront-hunter/analyze?${qs.toString()}`, { seller });
}

/**
 * GET /storefront-hunter/sellers/{id}/products
 */
export async function fetchStorefrontProducts(sellerId, opts = {}) {
  const page = opts.page ?? 1;
  const page_size = opts.pageSize ?? 40;
  const qs = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
  });
  return get(
    `/storefront-hunter/sellers/${encodeURIComponent(sellerId)}/products?${qs.toString()}`
  );
}

export async function fetchStorefrontCapabilities() {
  return get("/storefront-hunter/capabilities");
}

/**
 * POST /storefront-hunter/enrich — progressive batch after discovery render.
 * @param {{ seller_id?: string, item_ids: string[], fields?: string[] }} payload
 */
export async function enrichStorefrontItems(payload) {
  return post("/storefront-hunter/enrich", payload, { timeout: 45000 });
}

/**
 * POST /storefront-hunter/seller-offers — the analyzed seller's own offer price
 * for items whose Storefront Price is missing from the catalog.
 * @param {{ seller_id: string, seller_uuid: string, item_ids: string[] }} payload
 */
export async function fetchSellerOfferPrices(payload) {
  return post("/storefront-hunter/seller-offers", payload, { timeout: 45000 });
}

/**
 * Map API analysis payload into the existing Storefront Hunter UI bundle shape.
 * Never invents monthly_sold or added_days_ago.
 */
export function mapAnalysisToBundle(analysis) {
  if (!analysis || typeof analysis !== "object") {
    throw new Error("Invalid storefront analysis response");
  }
  const seller = analysis.seller || {};
  const sellerId = String(seller.seller_id || "");
  const products = (analysis.products || []).map((p, idx) => ({
    id: p.walmart_item_id || `sfh-${sellerId}-${idx}`,
    seller_id: sellerId,
    title: p.title ?? null,
    image_url: p.image_url ?? null,
    brand: p.brand ?? null,
    category: p.category ?? null,
    rating: p.rating ?? null,
    review_count: p.review_count ?? null,
    upc: p.upc ?? null,
    walmart_item_id: p.walmart_item_id ?? null,
    storefront_price: p.storefront_price ?? null,
    storefront_price_source:
      p.storefront_price_source ?? (p.storefront_price != null ? "catalog" : null),
    buy_box_price: p.buy_box_price ?? null,
    buy_box_owner: p.buy_box_owner ?? null,
    seller_count: p.seller_count ?? null,
    seller_count_source: p.seller_count_source ?? null,
    seller_count_raw_evidence: p.seller_count_raw_evidence ?? null,
    monthly_sold: p.monthly_sold == null ? null : p.monthly_sold,
    max_cost: p.max_cost ?? null,
    availability: p.availability ?? null,
    last_checked: p.last_checked ?? null,
    added_days_ago: p.added_days_ago == null ? null : p.added_days_ago,
    sources: [], // Phase 2: never auto-search Buy Sources
    enrichment: p.enrichment || {
      buy_box: p.buy_box_price != null ? "complete" : "idle",
      seller_count: p.seller_count != null ? "complete" : "idle",
      brand: p.brand ? "complete" : "idle",
      category: p.category ? "complete" : "idle",
      demand: p.monthly_sold != null ? "complete" : "idle",
    },
  }));

  const catalogScope = analysis.catalog_scope || null;
  const catalogComplete = analysis.catalog_complete === true;
  // Featured SSR products must never invent a full catalog product_count/total
  const productCount =
    catalogScope === "featured"
      ? null
      : seller.product_count ?? analysis.pagination?.total ?? null;

  const store = {
    seller_id: sellerId,
    seller_uuid: seller.seller_uuid ?? null,
    seller_name: seller.seller_name ?? null,
    rating: seller.rating ?? null,
    review_count: seller.review_count ?? null,
    product_count: productCount,
    last_checked: seller.last_checked ?? null,
    last_visited: seller.last_visited ?? null,
  };

  const historyEntry = {
    seller_id: sellerId,
    seller_name: store.seller_name,
    rating: store.rating,
    review_count: store.review_count,
    product_count: store.product_count,
  };

  const pagination = {
    ...(analysis.pagination || {
      page: 1,
      page_size: 40,
      total: null,
      total_pages: null,
      has_previous: false,
      has_next: false,
      next_cursor: null,
    }),
  };
  if (catalogScope === "featured") {
    pagination.total = null;
    pagination.total_pages = null;
    pagination.has_next = false;
  }

  return {
    store,
    storesBySeller: sellerId ? { [sellerId]: store } : {},
    brands: analysis.top_brands || [],
    categories: analysis.top_categories || [],
    catalogFacets: analysis.catalog_facets || null,
    products,
    sellers: sellerId ? [historyEntry] : [],
    pagination,
    catalog_status: analysis.catalog_status || "ok",
    catalog_scope: catalogScope || "featured",
    catalog_complete: false,
    product_source: analysis.product_source || null,
    dataSource: "api",
    note: null,
  };
}

/**
 * Extract a stable user-facing error message from axios/API failures.
 */
/** Valid seller id, but Walmart's storefront could not be loaded or verified. */
export const STOREFRONT_LOAD_FAILED_MESSAGE =
  "Could not load this Walmart storefront. Try again in a moment.";

const STOREFRONT_LOAD_FAILURE_CODES = new Set([
  "seller_catalog_provider_error",
  "seller_catalog_parse_error",
  "seller_identity_mismatch",
  "seller_catalog_rate_limited",
]);

export function storefrontErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    // Provider/parser wording is internal; the seller id itself was valid.
    if (STOREFRONT_LOAD_FAILURE_CODES.has(detail.code)) return STOREFRONT_LOAD_FAILED_MESSAGE;
    if (detail.message) return detail.message;
    if (detail.code === "seller_catalog_unavailable") {
      return "Walmart seller catalog is not available through current Ezisell integrations yet.";
    }
    if (detail.code === "invalid_seller_input") {
      return detail.message || "Invalid seller URL or Seller ID.";
    }
    if (detail.code === "seller_not_found") {
      return detail.message || "Seller not found.";
    }
  }
  if (typeof detail === "string") return detail;
  if (err?.code === "ECONNABORTED") return "Storefront request timed out.";
  return err?.message || "Failed to analyze storefront.";
}

/* ── Saved products (workspace bookmarks) ────────────────────────────────
   Persistence only: these endpoints never trigger provider work. */
const SAVED_BASE = "/storefront-hunter/saved-products";

export const fetchSavedProducts = (limit = 500) =>
  get(`${SAVED_BASE}?limit=${encodeURIComponent(limit)}`);

/** Batch saved-state for many item ids — one request, never N+1. */
export const fetchSavedProductStatus = (itemIds) =>
  post(`${SAVED_BASE}/status`, { item_ids: itemIds });

export const saveStorefrontProduct = (fields) => post(SAVED_BASE, fields);

export const unsaveStorefrontProduct = (walmartItemId) =>
  del(`${SAVED_BASE}/${encodeURIComponent(walmartItemId)}`);
